import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, Stack, CircularProgress, Alert,
} from "@mui/material";
import { getAuth } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";

const COLORS = {
  white: "#ffffff", whiteSoft: "#fff8ff", subtle: "#e6dfef",
  red: "#ff3333", textMain: "#2d233a", textMuted: "#6c6478",
};

export default function EstadisticasEstatal() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error("No autenticado");
        const token = await user.getIdToken();

        const res = await fetch(`${API_BASE}/users/estadisticas-estado`, {
          headers: { Authorization: `Bearer ${token}`, "x-firebase-uid": user.uid },
        });
        if (!res.ok) throw new Error("Error al obtener estadísticas");
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress sx={{ color: COLORS.red }} />
    </Box>
  );

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const { totales, programas, estados } = data;

  const cards = [
    { label: "Total usuarios",  value: totales.total,      color: "#3b82f6", bg: "#eff6ff" },
    { label: "Activos",         value: totales.activos,    color: "#22c55e", bg: "#f0fdf4" },
    { label: "Inactivos",       value: totales.inactivos,  color: "#f59e0b", bg: "#fffbeb" },
    { label: "Rechazados",      value: totales.rechazados, color: "#ef4444", bg: "#fef2f2" },
  ];

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{
        p: 2, borderRadius: 3, mb: 2,
        background: `linear-gradient(180deg, ${COLORS.whiteSoft} 0%, ${COLORS.white} 100%)`,
        border: `1px solid ${COLORS.subtle}`,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 900, color: COLORS.textMain, letterSpacing: 1.2 }}>
          ESTADÍSTICAS DE MI ESTADO
        </Typography>
        <Typography sx={{ color: COLORS.textMuted, mt: 0.3, fontSize: 13 }}>
          {estados.length === 1
            ? `Estado: ${estados[0]}`
            : `Estados: ${estados.join(", ")}`}
        </Typography>
      </Paper>

      {/* Cards */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        {cards.map(c => (
          <Box key={c.label} sx={{
            flex: 1, backgroundColor: c.bg, borderRadius: 3, p: 2.5,
            border: `1px solid ${c.color}22`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}>
            <Typography sx={{ fontSize: "0.78rem", color: "#666", mb: 0.5 }}>{c.label}</Typography>
            <Typography sx={{ fontSize: "2.2rem", fontWeight: 900, color: c.color, lineHeight: 1 }}>
              {c.value.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Tabla por programa */}
      <Paper elevation={0} sx={{
        borderRadius: 3, border: `1px solid ${COLORS.subtle}`, overflow: "hidden",
      }}>
        {/* Header tabla */}
        <Box sx={{
          display: "grid", gridTemplateColumns: "2fr 1fr",
          px: 2, py: 1.2, backgroundColor: COLORS.whiteSoft,
          borderBottom: `1px solid ${COLORS.subtle}`,
        }}>
          {["Programa", "Inscritos"].map(h => (
            <Typography key={h} sx={{ fontSize: "0.75rem", fontWeight: 900, color: COLORS.textMain, textTransform: "uppercase" }}>
              {h}
            </Typography>
          ))}
        </Box>

        {programas.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ color: COLORS.textMuted }}>Sin inscripciones registradas</Typography>
          </Box>
        ) : programas.map((p, idx) => (
          <Box key={p.program_id} sx={{
            display: "grid", gridTemplateColumns: "2fr 1fr",
            px: 2, py: 1.4, backgroundColor: COLORS.white,
            borderBottom: idx < programas.length - 1 ? `1px solid ${COLORS.subtle}` : "none",
            "&:hover": { backgroundColor: COLORS.whiteSoft }, transition: "0.15s",
          }}>
            <Typography sx={{ fontSize: "0.88rem", color: COLORS.textMain, fontWeight: 500 }}>
              {p.programa}
            </Typography>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: COLORS.red }}>
              {p.total.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}